"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, useRef } from "react";
import { cn } from "@/lib/utils";
import { Play, Pause, Headphones, AudioLines, Check } from "lucide-react";
const WORDS = "The clash of steel echoed through the valley as Arthur raised his blade against the coming storm".split(" ");
const TOTAL_SECONDS = 252; // 4:12

/*
 * A static, lively waveform pattern (percent heights). Hardcoded as integers
 * rather than computed with Math.sin() at module load: transcendental math is
 * not guaranteed bit-identical between the SSR (Node) and browser engines, and
 * the tiny float divergence surfaces as a React hydration mismatch on the
 * inline height style.
 */
const BAR_HEIGHTS = [36, 52, 64, 46, 30, 44, 60, 74, 58, 40, 28, 48, 66, 80, 62, 42, 26, 40, 58, 72, 54, 34, 30, 50, 68, 78, 56, 38, 28, 48, 64, 52, 36, 44];
const BAR_COUNT = BAR_HEIGHTS.length;
function formatTime(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}
export function AudiobookDemo() {
  const $ = _c(47);
  const [step, setStep] = useState(0);
  const [wordIdx, setWordIdx] = useState(0);
  const containerRef = useRef(null);
  const cancelledRef = useRef(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      cancelledRef.current = false;
      const wait = _temp;
      async function runAnimation() {
        const isCancelled = () => cancelledRef.current;
        setStep(0);
        setWordIdx(0);
        await wait(600);
        if (isCancelled()) {
          return;
        }
        setStep(1);
        await wait(1100);
        if (isCancelled()) {
          return;
        }
        setStep(2);
        for (let i = 1; i <= WORDS.length; i++) {
          if (isCancelled()) {
            return;
          }
          setWordIdx(i);
          await wait(250);
        }
        await wait(1800);
        if (isCancelled()) {
          return;
        }
        if (!isCancelled()) {
          runAnimation();
        }
      }
      const observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          runAnimation();
        }
      }, {
        threshold: 0.4
      });
      if (containerRef.current) {
        observer.observe(containerRef.current);
      }
      return () => {
        cancelledRef.current = true;
        observer.disconnect();
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  const playing = step >= 2;
  const ratio = wordIdx / WORDS.length;
  const currentSeconds = ratio * TOTAL_SECONDS;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-[size:20px_20px] opacity-[0.03]" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = step >= 1 ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0";
  let t4;
  if ($[3] !== t3) {
    t4 = cn("relative overflow-hidden rounded-2xl border border-border bg-card/90 p-5 shadow-inner backdrop-blur-xl transition-all duration-700", t3);
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-brand/15 text-brand"><Headphones className="size-5" /></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="truncate text-sm font-bold text-foreground">Chapter 7 — The Ember Throne</div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="mb-4 flex items-center gap-3">{t5}<div className="min-w-0">{t6}<div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground"><AudioLines className="size-3" /> AI narration</div></div><span className="ml-auto rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-500">Ready</span></div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== wordIdx) {
    t8 = WORDS.map((word, i_0) => <span key={`${word}-${i_0}`} className={cn("transition-colors duration-200", i_0 < wordIdx ? "text-foreground" : "text-muted-foreground/40", i_0 === wordIdx - 1 && "rounded bg-brand/15 px-0.5 font-semibold text-brand")}>{word}{" "}</span>);
    $[8] = wordIdx;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t8) {
    t9 = <div className="mb-4 rounded-xl border border-border/60 bg-background/60 p-4 font-serif text-sm leading-relaxed">{t8}</div>;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== playing || $[13] !== ratio) {
    t10 = BAR_HEIGHTS.map((h, i_1) => {
      const active = i_1 / BAR_COUNT <= ratio;
      return <div key={i_1} className={cn("flex-1 rounded-full transition-all duration-300", active ? "bg-gradient-to-t from-brand to-brand-light" : "bg-muted", active && playing && "animate-pulse")} style={{
        height: `${active ? h : Math.round(h * 0.55)}%`
      }} />;
    });
    $[12] = playing;
    $[13] = ratio;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10) {
    t11 = <div className="mb-3 flex h-14 items-center justify-between gap-[3px]">{t10}</div>;
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== playing) {
    t12 = <button type="button" tabIndex={-1} aria-hidden="true" className="flex size-11 shrink-0 items-center justify-center rounded-full bg-accent text-white shadow-lg shadow-accent/30">{playing ? <Pause className="size-5" fill="currentColor" /> : <Play className="size-5 pl-0.5" fill="currentColor" />}</button>;
    $[17] = playing;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const t13 = `${ratio * 100}%`;
  let t14;
  if ($[19] !== t13) {
    t14 = <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-gradient-to-r from-brand to-brand-light transition-[width] duration-200 ease-linear" style={{
        width: t13
      }} /></div>;
    $[19] = t13;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== currentSeconds) {
    t15 = formatTime(currentSeconds);
    $[21] = currentSeconds;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== t15) {
    t16 = <span>{t15}</span>;
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <span>{formatTime(TOTAL_SECONDS)}</span>;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== t16) {
    t18 = <div className="mt-1.5 flex justify-between text-[10px] font-medium tabular-nums text-muted-foreground">{t16}{t17}</div>;
    $[26] = t16;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== t14 || $[29] !== t18) {
    t19 = <div className="flex-1">{t14}{t18}</div>;
    $[28] = t14;
    $[29] = t18;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== t12 || $[32] !== t19) {
    t20 = <div className="flex items-center gap-3">{t12}{t19}</div>;
    $[31] = t12;
    $[32] = t19;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== t11 || $[35] !== t20 || $[36] !== t4 || $[37] !== t9) {
    t21 = <div className="relative w-full max-w-2xl flex-1 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-bl from-indigo-900/20 via-background to-purple-900/10 p-4 shadow-2xl before:pointer-events-none before:absolute before:inset-0 before:bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] before:from-indigo-500/10 before:via-transparent before:to-transparent">{t2}<div className={t4}>{t7}{t9}{t11}{t20}</div></div>;
    $[34] = t11;
    $[35] = t20;
    $[36] = t4;
    $[37] = t9;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  let t23;
  let t24;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand/10 px-3 py-1 text-xs font-semibold text-brand"><Headphones className="size-3.5" />Text-to-speech</div>;
    t23 = <h2 className="font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">Every chapter, instantly narrated</h2>;
    t24 = <p className="text-lg leading-relaxed text-muted-foreground">One click turns any chapter into natural-sounding audio. Readers can listen anywhere — another way for your story to reach them.</p>;
    $[39] = t22;
    $[40] = t23;
    $[41] = t24;
  } else {
    t22 = $[39];
    t23 = $[40];
    t24 = $[41];
  }
  let t25;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <li className="flex items-start gap-3"><div className="mt-1 shrink-0 rounded-full bg-indigo-500/20 p-1 text-indigo-400"><AudioLines className="size-4" /></div><div><span className="font-semibold text-foreground">One-click audio:</span>{" "}Generate narration for any chapter — no recording, no microphone.</div></li>;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  let t26;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <li className="flex items-start gap-3"><div className="mt-1 shrink-0 rounded-full bg-purple-500/20 p-1 text-purple-400"><Headphones className="size-4" /></div><div><span className="font-semibold text-foreground">Listen anywhere:</span>{" "}Readers press play and keep going, hands-free.</div></li>;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  let t27;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <div className="max-w-lg flex-1 space-y-6">{t22}{t23}{t24}<ul className="mt-8 space-y-4">{t25}{t26}<li className="flex items-start gap-3"><div className="mt-1 shrink-0 rounded-full bg-emerald-500/20 p-1 text-emerald-400"><Check className="size-4" strokeWidth={3} /></div><div><span className="font-semibold text-foreground">Reach more readers:</span>{" "}Give commuters and multitaskers a way into your story.</div></li></ul></div>;
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  let t28;
  if ($[45] !== t21) {
    t28 = <section ref={containerRef} className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-12 px-6 pt-24 pb-12 md:flex-row-reverse lg:px-12">{t21}{t27}</section>;
    $[45] = t21;
    $[46] = t28;
  } else {
    t28 = $[46];
  }
  return t28;
}
function _temp(ms) {
  return new Promise(r => setTimeout(r, ms));
}